/**
 * @module app.searchDirective
 */
import {unByKey} from 'ol/Observable.js';
import appBase from './index.js';
import {
  capitalize,
  isMapExtentInclude,
  isWinterLayer,
  lv95ExtentToMapProjection,
  lv95ToMapProjection
} from './utils.js';
import olFeature from 'ol/Feature.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olGeomPoint from 'ol/geom/Point.js';
import {SEARCH_LABEL_MAX_SIZE} from "./constants";
import {equals} from "ol/array";
import {LineString} from 'ol/geom.js';

const emptyGeom = new LineString([]);


/**
 * @type {RegExp}
 */
const WKT_BOX_RE = /BOX\(([\d\.']+)\s+([\d\.']+)[\s,]([\d\.']+)\s+([\d\.']+)\)/;

const codeToLand = {
  "mountainbike": "mountainbike",
  "winterhike": "winterhiking",
  "snowshoe": "snowshoe",
  "sledge": "sledging",
  "hike": "wander",
  "cycle": "velo",
  "crosscountry": "crosscountry",
  "canoe": "kanu",
  "skate": "skating",
};

const landToCode = Object.entries(codeToLand).reduce((previous, current) => {
  const [k, v] = current;
  previous[v] = k;
  return previous;
}, {});

/**
 * @const
 */
export const ES_KINDS = {
  'wander': 'Wanderland',
  'velo': 'Veloland',
  'mountainbike': 'Mountainbikeland',
  'skating': 'Skatingland',
  'kanu': 'Kanuland',
  'winterhiking': 'Winterhiking',
  'snowshoe': 'Snowshoe',
  'crosscountry': 'Crosscountry',
  'sledging': 'Sledging',
  'accomodation': 'Accommodation',
  'accommodation': 'Accommodation',
  'place': 'Places',
  'sightseeing': 'Sightseeing',
  'velorental': 'Velorental',
  'cycleservice': 'Cycleservice',
  'route': 'Route',
  'segment': 'Segment',
  'mountaineering': 'Mountaineering',
  'ski_tour': 'Ski Tour'
};


/**
 * @const
 */
export const ACCOMMODATION_TYPE = {
  '1': 'hotel',
  '2': 'bedbreakfast',
  '3': 'youthhostel',
  '4': 'backpacker',
  '5': 'groupaccom',
  '6': 'sleepingstraw',
  '7': 'farmaccom',
  '8': 'vacationapptmt',
  '9': 'mountainhut',
  '10': 'campingsite'
};


/**
 * @const
 */
export const LAYERS_NAMES = {
  'wander': 'Wanderland',
  'velo': 'Veloland',
  'mountainbike': 'Mtbland',
  'skating': 'Skatingland',
  'kanu': 'Kanuland'
};


/**
 * @constructor
 * @param {angular.Scope} $scope Scope.
 * @param {string} apiUrl API url prefix.
 * @param {angularGettext.Catalog} gettextCatalog Gettext catalog.
 * @param {app.MapService} appMapService
 * @param {app.HighlightFeature} appHighlightFeature
 * @ngInject
 * @struct
 * @private
 */
class SearchController {

  constructor($scope, apiUrl, gettextCatalog, appMapService, appHighlightFeature) {

    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    /**
     * @type {string}
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @type {angularGettext.Catalog}
     */
    this.gettextCatalog = gettextCatalog;

    /**
     * @private
     * @type {app.MainController}
     */
    this.mainCtrl_ = $scope.$root['mainCtrl'];

    /**
     * @type {ol.Map}
     * @private
     */
    this.map_ = appMapService.map;

    /**
     * @type {ol.Feature}
     * @private
     */
    this.highlightedFeature_ = new olFeature();

    /**
     * @type {app.HighlightFeature}
     * @private
     */
    this.appHighlight_ = appHighlightFeature;
    console.assert(appHighlightFeature.initialized);
    this.appHighlight_.addFeature(this.highlightedFeature_, 'searchHighlight', {
      'isProtected': true
    });

    /**
     * @type {ol.EventsKey|Array.<ol.EventsKey>}
     * @private
     */
    this.highlightEventKey_ = null;

    /**
     * @type {number}
     */
    this.limit = 30;

    /**
     * @type {string}
     * @export
     */
    this.input_value = '';

    /**
     * @type {Bloodhound}
     * @private
     */
    this.geoadminBloodhound_ = new Bloodhound(/** @type {BloodhoundOptions} */({
      queryTokenizer: Bloodhound.tokenizers.whitespace,
      datumTokenizer: Bloodhound.tokenizers.obj.whitespace('detail'),
      remote: {
        url: 'https://api3.geo.admin.ch/rest/services/api/SearchServer',
        replace: (url, query) => {
          return url +
              '?type=locations' +
              '&origins=kantone,district,zipcode,gg25,gazetteer,address' +
              '&sr=2056' +
              '&searchText=' + encodeURIComponent(query) +
              '&lang=' + gettextCatalog.currentLanguage +
              '&callback=?';
        },
        filter: (resp) => {
          const datums = /** @type {Array.<BloodhoundDatum>} */ (resp['results']);
          return datums.map((datum) => {
            const attributes = datum['attrs'];
            let label = attributes['label'].replace(/<\/?b>/g, '');
            if (label.length > SEARCH_LABEL_MAX_SIZE) {
              label = label.slice(0, SEARCH_LABEL_MAX_SIZE - 4) + ' ...';
            }
            let prefix = '';
            if (attributes['origin'] === 'kantone') {
              prefix = gettextCatalog.getString('Ct') + '. ';
            } else if (attributes['origin'] === 'district') {
              prefix = gettextCatalog.getString('District') + ' ';
            }
            attributes['label'] = prefix + label;
            // create a simple label without html to be displayed
            // when the item is selected
            attributes['simple_label'] = attributes['label'].replace(/<\/?i>/g, '');
            attributes['tt_source'] = 'geoadmin';
            attributes['kind'] = 'geoadmin';
            attributes['_type'] = 'geoadmin';

            return attributes;
          });
        }
      }
    }));
    this.geoadminBloodhound_.initialize();

    /**
     * @type {Object.<string, Bloodhound>}
     * @private
     */
    this.esBloodhounds_ = {};
    this.initializeEsBloodhounds_();

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.format_ = new olFormatGeoJSON();
  }


  /**
   * @private
   * @param {ol.View} view
   * @return {function(string, function(Array.<number>))}
   */
  createSearchCoordinates_(view) {
    return (query, callback) => {
      const suggestions = [];
      const match = query.match(/([\d\.']+)[\s,\/]+([\d\.']+)/);
      if (match) {
        const left = parseFloat(match[1].replace(/\'/g, ''));
        const right = parseFloat(match[2].replace(/\'/g, ''));

        if (!isFinite(left) || !isFinite(right)) {
          return undefined;
        }

        // reorder coordinates
        const coordinates = [Math.max(left, right), Math.min(left, right)];

        // try with coordinates in EPSG:21781 and EPSG:2056
        [coordinates, lv95ToMapProjection(coordinates)].forEach((proj_coordinates) => {
          if (isMapExtentInclude(proj_coordinates)) {
            view.setCenter(proj_coordinates);
            const constrainted = view.getCenter();
            console.assert(constrainted);
            if (equals(proj_coordinates, constrainted)) {
              // inside the view extent
              suggestions.push({
                'label': this.gettextCatalog.getString('Recenter to') + ' ' + coordinates.map(Math.round).join(' '),
                'x': proj_coordinates[1],
                'y': proj_coordinates[0],
                'tt_source': 'coordinates',
                '_type': 'coordinates',
                'kind': 'coordinates'
              });
            }
          }
        });
      }
      callback(suggestions);
    };
  }


  /**
   * @private
   * @param {string} kind
   * @return {Bloodhound}
   */
  esBloodhoudFactory_(kind) {
    const searchEndpoint = '4/fts_search';
    const getSeason = () => this.mainCtrl_.season;
    // the '+1' is a workaround for the typeahead issue:
    // https://github.com/twitter/typeahead.js/issues/1312
    const limit = '' + (this.limit + 1);
    return new Bloodhound(/** @type {BloodhoundOptions} */({
      remote: {
        url: this.apiUrl_ + searchEndpoint,
        replace: (url, query) => {
          return url +
              '?qtext=' + encodeURIComponent(query) +
              '&limit=' + limit +
              '&lang=' + this.gettextCatalog.currentLanguage +
              '&season=' + getSeason();
        },
        filter: (resp) => {
          const datums = /** @type {Array.<BloodhoundDatum>} */ (resp);
          const filteredList = [];

          for (let i = 0, ii = datums.length; i < ii; i++) {
            const datum = datums[i];
            if (datum['kind'] == kind) {

              const source = datum['_source'];
              datum['label'] = '';

              if (datum['_type'] === 'route') {
                datum['label'] += source['number'] + ' ';
              }
              if (datum['_type'] === 'segment') {
                source['title'] = source['title_admin'];
              }

              datum['label'] += source['title'];

              if (source['address_city']) {
                datum['label'] += ' - ' + source['address_city'];
              }

              if (source['category']) {
                datum['category'] = source['category'];
              }
              if (source['route_category']) {
                datum['category'] = source['route_category'];
              }

              if (source['type_ids']) {
                datum['type_ids'] = source['type_ids'];
              }

              datum['tt_source'] = 'elasticsearch';

              filteredList.push(datum);
            }
          }
          return filteredList;
        }
      },
      queryTokenizer: Bloodhound.tokenizers.whitespace,
      datumTokenizer: Bloodhound.tokenizers.obj.whitespace('detail')
    }));
  }


  /**
   * @private
   */
  initializeEsBloodhounds_() {
    let esKind;
    for (esKind in ES_KINDS) {
      const esBloodhound = this.esBloodhoudFactory_(esKind);
      this.esBloodhounds_[esKind] = esBloodhound;
      esBloodhound.initialize();
    }
  }

  /**
   * @private
   */
  adaptBloodhound_(bloodhound, minLength) {
    const source = bloodhound.ttAdapter();
    return function(query, b, c) {
      if (query.trim().length >= minLength) {
        source(query, b, c);
      }
    };
  }

  /**
   * @private
   * @param {string} esKind
   * @return {TypeaheadDataset}
   */
  esFactory_(esKind) {
    const kind = ES_KINDS[esKind];
    return /** @type {TypeaheadDataset} */ ({
      name: 'elasticsearch',
      display: 'label',
      limit: this.limit,
      source: this.adaptBloodhound_(this.esBloodhounds_[esKind], 1),
      templates: {
        header: () => {
          return '<div class="header ' + esKind + '">' +
              this.gettextCatalog.getString(kind) + '</div>';
        }
      }
    });
  }

  async fetchRouteOrSegmentGeoMultiline(datum, routeNumber, segmentNumber) {
    const land = datum.kind;
    const landCode = landToCode[land];
    const response = await fetch(`${this.apiUrl_}4/route_or_segment/${landCode}/${routeNumber}/${segmentNumber}.json`);
    const json = await response.json();
    const geometry = await this.format_.readGeometry(json);
    datum.geometry = geometry;
    return geometry;
  }

  /**
   * @private
   * @param {BloodhoundDatum} datum
   * @param {boolean=} fetchIfNeeded
   * @return {ol.geom.Geometry}
   */
  getGeometry_(datum, fetchIfNeeded) {
    if (datum.geometry) {
      return datum.geometry;
    }
    const source = datum['_source'];
    if (source) {
      // schweizmobil
      const field = source['geo_point'] || source['geo_multiline'];
      if (!field && fetchIfNeeded && !datum.geomPromise) {
        if (source.kind == 'route') {
          datum.geomPromise = this.fetchRouteOrSegmentGeoMultiline(datum, source.number, 0);
        }
        if (source.kind == 'segment') {
          datum.geomPromise = this.fetchRouteOrSegmentGeoMultiline(datum, source.route_number, source.number);
        }
      }
      datum.geometry = this.format_.readGeometry(field);
    } else {
      const kind = datum['kind'];
      if (kind === 'geoadmin') {
        datum.geometry = new olGeomPoint(lv95ToMapProjection([datum['y'], datum['x']]));
      } else if (kind === 'coordinates') {
        datum.geometry = new olGeomPoint([datum['y'], datum['x']]);
      } else {
        console.assert(false, 'unknown datum kind', kind);
      }
    }
    return datum.geometry;
  }


  /**
   * @private
   * @param {BloodhoundDatum} datum
   * @param {boolean} isForNewFeature
   * @return {string}
   */
  getHighlightKind_(datum, isForNewFeature) {
    let kind = /** @type {string} */ (datum['kind']);
    if (datum['_type'] === 'route' && isForNewFeature) {
      kind = 'transparent';
    } else if (kind === 'accomodation' || kind === 'accommodation') {
      const types = datum['_source']['type_ids'] || datum['_source']['types'];
      console.assert(types);
      for (let i = 0, ii = types.length; i < ii; i++) {
        const type = ACCOMMODATION_TYPE[types[i]] || types[i];
        if (type) {
          kind = type;
          break;
        }
      }
      // type not found in list
      console.assert(kind != 'poi');
    } else if (kind == 'mountaineering' || kind === 'ski_tour') {
      let sac_type = datum._source.sac_type;
      sac_type = sac_type.includes('climbing') ? 'climbing' : sac_type;
      kind = sac_type.replace('_', '');
    }
    return kind;
  }


  /**
   * @private
   * @param {BloodhoundDatum} datum
   * @return {string}
   */
  getLayerName_(datum) {
    let layername;
    const type = /** @type {string} */ (datum['_type']);
    const kind = /** @type {string} */ (datum['kind']);
    if (type === 'route' || type === 'segment') {
      // special case for winter layers: the layer name contains no 'Etappen<category>'
      // but simply the layer name. It will change when these layers will contain more than
      // one category
      if (isWinterLayer(kind)) {
        layername = capitalize(kind);
      } else {
        let land = LAYERS_NAMES[kind];
        land = capitalize(land);
        let category = capitalize(/** @type {string} */ (datum['category']));
        if (category === 'Local') {
          category = 'Lokal';
        }
        layername = land + 'Etappen' + category;
      }
    } else if (kind === 'accomodation' || kind === "accommodation") {
      if (datum['_source']['type_ids']) {
        layername = datum['_source']['type_ids'][0];
        layername = ACCOMMODATION_TYPE[layername];
      } else {
        layername = datum['_source']['types'][0];
      }
      layername = capitalize(layername);
    } else {
      layername = capitalize(kind);
    }
    return layername;
  }


  /**
   * @private
   * @param {BloodhoundDatum} datum
   * @return {string}
   */
  getLayerGroupName_(datum) {
    let layerGroupName;
    const type = datum['_type'];
    const kind = /** @type {string} */ (datum['kind']);
    if (type === 'route' || type === 'segment') {
      layerGroupName = kind + (isWinterLayer(kind) ? '' : 'land');
    } else {
      if (kind === 'accomodation' || kind === "accommodation" || kind === 'mountaineering') {
        layerGroupName = kind;
      } else if (kind === 'ski_tour') {
        return 'SkiTour';
      } else {
        layerGroupName = 'services';
      }
    }
    return capitalize(layerGroupName);
  }


  /**
   * @private
   * @param {string} layerGroupName
   */
  activateLayerGroup_(layerGroupName) {
    if (isWinterLayer(layerGroupName.toLowerCase())) {
      this.mainCtrl_.wms.layers[layerGroupName] = true;
    } else {
      this.mainCtrl_.wms.groups[layerGroupName] = true;
    }
  }


  /**
   * @private
   * @param {BloodhoundDatum} datum
   */
  addHighlightToMap_(datum) {
    const layername = this.getLayerName_(datum);
    const geom = /** @type {ol.geom.Geometry} **/ (datum['geometry']);
    const kind = this.getHighlightKind_(datum, true);
    const hlId = 'search_' + layername + datum['id'];
    if (geom) {
      const feature = new olFeature(geom);
      const config = {
        'kind': kind,
        'layer': layername
      };
      this.addSignalWay_(config, datum);
      this.appHighlight_.addFeature(feature, hlId, config);
    }
    //remove feature when map move
    if (this.highlightEventKey_) {
      unByKey(this.highlightEventKey_);
    }
    this.highlightEventKey_ = this.map_.on('pointermove', this.clearHighlight_.bind(this), this);
  }


  /**
   * @private
   * @param {ol.MapBrowserPointerEvent} evt
   */
  clearHighlight_(evt) {
    if (evt.dragging) {
      this.appHighlight_.clear();
      unByKey(this.highlightEventKey_);
      this.highlightEventKey_ = null;
    }
  }


  /**
   * @param {Object<string, (boolean|string)>} config
   * @param {BloodhoundDatum} datum
   */
  addSignalWay_(config, datum) {
    if (datum['_source'] && datum['_source']['signal_way']) {
      config['signal_way'] = datum['_source']['signal_way'];
    }
  }


  /**
   * @export
   */
  clearSearch() {
    const typeahead = $('.twitter-typeahead');
    const ttmenu = typeahead.children('.tt-menu');
    const inputs = typeahead.children('.input');
    this.setTTDropdownVisibility(false);
    // clear model value, the 'real' input value and tt's suggestions
    this.input_value = '';
    $(inputs[1]).typeahead('val', '');
    ttmenu.children('.tt-dataset').empty();
  }


  /**
   * @export
   * @param {boolean} visible
   */
  setTTDropdownVisibility(visible) {
    const ttDropdown = $('.twitter-typeahead .tt-menu');
    (visible) ? ttDropdown.show() : ttDropdown.hide();
  }

}

appBase.controller('appSearchController', SearchController);


/**
 * @param {angular.$sanitize} sanitize Angular sanitize service.
 * @return {angular.Directive} The directive specs.
 * @ngInject
 */
const appSearch = function(sanitize) {
  const esKinds = Object.keys(ES_KINDS);
  return {
    restrict: 'A',
    controller: 'appSearchController',
    controllerAs: 'searchCtrl',
    bindToController: true,
    link: (scope, element, attrs, searchCtrl) => {
      const view = searchCtrl.map_.getView();
      let displayTTDropdown = true;
      const datasets = [{
        name: 'coordinates',
        display: 'label',
        source: searchCtrl.createSearchCoordinates_(view)
      }];
      if (!new URLSearchParams(document.location.search).has('nogeoadminsearch')) {
        datasets.push({
          name: 'geoadmin',
          display: 'simple_label',
          limit: searchCtrl.limit,
          source: searchCtrl.adaptBloodhound_(searchCtrl.geoadminBloodhound_, 2),
          templates: {
            suggestion: (datum) => {
              const label = sanitize(datum.label);
              return $("<div>").html(label).addClass('tt-suggestion');
            }
          }
        });
      }
      for (let i = 0; i < esKinds.length; i++) {
        datasets.push(searchCtrl.esFactory_(esKinds[i]));
      }

      element.typeahead({
        highlight: true,
        minLength: 0 // see adaptBloodhound_ function
      }, datasets);

      element.on('typeahead:select', function(event, datum, dataset) {
        const mapSize = searchCtrl.map_.getSize();
        console.assert(mapSize);

        // FIXME remove tt_source from datums when typeahead 0.12 will be
        // released see (https://github.com/twitter/typeahead.js/issues/1202)
        dataset = datum['tt_source'];
        const geometry = searchCtrl.getGeometry_(datum, true);
        datum.geometry = geometry;

        const match = (datum['geom_st_box2d'] || '').match(WKT_BOX_RE);
        if (match) {
          console.assert(dataset === 'geoadmin');
          const box = match.slice(1).map(parseFloat);
          view.fit(lv95ExtentToMapProjection(box), {size: mapSize});
        } else {
          if (geometry) {
            if (geometry.getType() === 'Point') {
              view.setCenter(geometry.getCoordinates());
              view.setZoom(8);
            } else {
              // recenter on route
              const extent = geometry.getExtent();
              view.fit(extent, {size: mapSize});
            }
          } else if (datum.geomPromise) {
            datum.geomPromise.then(geometry => {
              // recenter on route
              const extent = geometry.getExtent();
              view.fit(extent, {size: mapSize});
              searchCtrl.addHighlightToMap_(datum); // redo with filled geometry
            });
          }
          if (dataset !== 'coordinates') {
            const layerGroupName = searchCtrl.getLayerGroupName_(datum);
            searchCtrl.activateLayerGroup_(layerGroupName);
          }
        }

        searchCtrl.addHighlightToMap_(datum);
        displayTTDropdown = false;
        searchCtrl.setTTDropdownVisibility(displayTTDropdown);

        gtag('event', 'search_select', {
          'event_label': `${datum['kind']}:${datum['label']}`
        });

      });

      element.on('typeahead:change', (event, datum, dataset) => {
        //Sometimes, on clear, typeahead sends a "change" event with
        //a 'string' datum
        if (typeof datum === 'object') {
          const kind = searchCtrl.getHighlightKind_(datum, false);
          const hlfeature = searchCtrl.highlightedFeature_;
          const hlConfig = {
            'geom': searchCtrl.getGeometry_(datum) || emptyGeom,
            'kind': kind
          };
          searchCtrl.addSignalWay_(hlConfig, datum);
          searchCtrl.appHighlight_.setFeature(hlfeature, hlConfig);
        }
      });

      // new search
      element.on('typeahead:open', () => {
        searchCtrl.appHighlight_.clear();
      });

      element.on('typeahead:close', () => {
        searchCtrl.highlightedFeature_.setGeometry(null);
        searchCtrl.setTTDropdownVisibility(displayTTDropdown);
        displayTTDropdown = true;
      });

      // only show the search menu
      element.click((event) => {
        scope.$root['mainCtrl'].showMenu('search', true);
      });
    }
  };
};

appBase.directive('appSearch', ['$sanitize', appSearch]);


export default appSearch;
